import { faCircleCheck } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Card,
  Form,
  getTextInputProps,
  InputTitle,
  Page,
  SubTitle,
  Title,
} from '@foxtrotplatform/developer-platform-common-ui-component-lib';
import { Team, useTeam } from '@foxtrotplatform/platform-micro-site-core';
import { Button, HelperText } from '@windmill/react-ui';
import { FunctionComponent, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { updateTeam } from '../../../common/api/team';
import { getUserDeveloperTeams } from '../../../common/api/user';
import { addToast } from '../../../common/reducers/toasterSlice';

export interface TeamUpdate {
  teamName: string;
}

export const CreatedSuccesfully: FunctionComponent = (): JSX.Element | null => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { organizationId = '' } = useParams();
  const [team, setTeam] = useState<Team>();
  const { refreshTeams } = useTeam();

  const {
    handleSubmit,
    register,
    reset,
    setValue,
    formState: { errors },
  } = useForm<TeamUpdate>({
    mode: 'onBlur',
  });

  useEffect(() => {
    const getCreatedDevTeam = async () => {
      const { 0: team } = await getUserDeveloperTeams(organizationId);

      setTeam(team);
      setValue('teamName', team.name);
    };
    if (organizationId && !team) {
      getCreatedDevTeam();
    }
  }, [setValue, organizationId, team]);

  const onSubmit = handleSubmit(async ({ teamName }: TeamUpdate) => {
    if (teamName === team?.name) {
      //Team Name wasn't updated, no need to make API call
      reset();
      navigate(`/organization/${organizationId}/provisioning`);
      return;
    }

    try {
      const { id: teamId = '' } = team || {};
      await updateTeam(organizationId, teamId, teamName);
      refreshTeams();
      dispatch(
        addToast({
          message: `Successfully update team name: ${teamName}`,
        })
      );
      reset();
      navigate(`/organization/${organizationId}/provisioning`);
    } catch {
      dispatch(
        addToast({
          message: `failed to update team: ${teamName}`,
          isError: true,
        })
      );
    }
  });

  const hasError = !!errors?.teamName;
  const errorMessage = hasError && errors.teamName?.message;
  return (
    <Page>
      <Card className="flex flex-col p-os1-spacing w-os1-container">
        <div className="flex flex-row gap-x-3 mb-os1-spacing">
          <div className="h-7 w-12 pt-px pl-1 mt-xs bg-green-50">
            <FontAwesomeIcon
              className="fa-lg text-green-600"
              icon={faCircleCheck}
            />
          </div>
          <div>
            <Title className="mb-os1-spacing font-bold">
              Organization Created Successfully
            </Title>
            <SubTitle>
              <div>
                <div>
                  Your organization is created on our platform. We will now
                  review your request for the sandbox environment. Our admins
                  will reach out to you with a decision shortly after which you
                  can start developing your own Apps. Meanwhile, we have created
                  a team for you with a default name. If you would like to
                  update the team name then enter it below and click Update Team
                </div>
              </div>
            </SubTitle>
          </div>
        </div>

        <Form onSubmit={onSubmit} title="Update Team">
          <div className="flex flex-row gap-x-3 items-center">
            <div className="flex flex-col">
              <div>
                <InputTitle>Team Name</InputTitle>
              </div>
              <input
                {...getTextInputProps<TeamUpdate>({
                  inputKey: 'teamName',
                  errors,
                  register,
                })}
              />
              <span className="text-xs text-gray-700">
                You are the admin of this team
              </span>
            </div>
            <div>
              <Button
                className="h-10 w-40 mt-2"
                disabled={hasError}
                onClick={onSubmit}
              >
                Update Team
              </Button>
            </div>
          </div>
          {errorMessage && (
            <HelperText valid={false}>{errorMessage}</HelperText>
          )}
        </Form>
      </Card>
    </Page>
  );
};
